import { Modal, Text, Group, Button, Stepper, Stack } from '@mantine/core';
import { useState, useEffect } from 'react';
import cn from 'clsx';
import { useQueryClient } from '@tanstack/react-query';
import { IoArrowDownSharp } from 'react-icons/io5';
import { BiLinkExternal } from 'react-icons/bi';

import { useTokensInfo } from 'src/api/dex';
import { Quote, CallData } from 'src/types';
import { usePrepareAndSendTransaction } from 'src/hooks/usePrepareAndSendTransaction';
import { formatDecimal } from 'src/utils';
import { Token } from 'src/components/Token/Token';
import { NETWORK_VARS } from 'src/constants';

type ApprovalStep = {
  label: string;
  callData?: CallData;
  successNotificationMessage?: React.ReactNode;
  refetchOnSuccess?: boolean;
  onSuccess: (hash: string) => void;
};

export function SwapConfirmationModal({
  fromValue,
  toValue,
  fromToken,
  toToken,
  quote,
  onClose,
  onSuccess,
  onError,
}: {
  fromValue: string;
  toValue: string;
  fromToken: string;
  toToken: string;
  quote?: Quote;
  onClose: () => void;
  onSuccess: () => void;
  onError: (err: Error) => void;
}) {
  const queryClient = useQueryClient();

  const erc20ApproveCall = quote?.swapDetails?.calls?.find(
    (c) => c.type === 'erc20_approve'
  );
  const permitApproveCall = quote?.swapDetails?.calls?.find(
    (c) => c.type === 'permit2_approve'
  );
  const swapApproveCall = quote?.swapDetails?.calls?.find(
    (c) => c.type === 'swap'
  );
  const wrapMonCall = quote?.swapDetails?.calls?.find((c) => c.type === 'wrap');
  const unwrapMonCall = quote?.swapDetails?.calls?.find(
    (c) => c.type === 'unwrap'
  );

  const wrapOrUnwrapMonCall = wrapMonCall || unwrapMonCall;

  const [approvedFromValue, setApprovedFromValue] = useState<
    string | undefined
  >();
  const [approvedToValue, setApprovedToValue] = useState<string | undefined>();
  const [swapHash, setSwapHash] = useState<string | undefined>();

  const swapInitialStep = !!erc20ApproveCall ? 0 : !!permitApproveCall ? 1 : 2;

  const [activeStep, setActiveStep] = useState(
    wrapOrUnwrapMonCall ? 0 : swapInitialStep
  );

  const { getTokenByAddress } = useTokensInfo();

  const fromTokenInfo = getTokenByAddress(fromToken);
  const toTokenInfo = getTokenByAddress(toToken);

  const handleSuccess = (hash: string) => {
    setApprovedFromValue(fromValue);
    setApprovedToValue(toValue);
    setSwapHash(hash);
    queryClient.refetchQueries();
  };

  const steps: ApprovalStep[] = wrapOrUnwrapMonCall
    ? [
        {
          label: 'Approve transaction',
          callData: wrapOrUnwrapMonCall,
          successNotificationMessage: (
            <>
              You successfully{' '}
              {wrapOrUnwrapMonCall.type === 'wrap' ? 'wrapped' : 'unwrapped'}{' '}
              {formatDecimal(approvedFromValue || fromValue, {
                withTruncate: true,
              })}{' '}
              MON
            </>
          ),
          onSuccess: (hash) => {
            setActiveStep(1);
            handleSuccess(hash);
          },
        },
      ]
    : [
        {
          label: 'Approve ERC20 for spending',
          callData: erc20ApproveCall,
          successNotificationMessage: 'ERC20 approved',
          onSuccess: () => setActiveStep(!!permitApproveCall ? 1 : 2),
        },
        {
          label: 'Approve permit',
          callData: permitApproveCall,
          successNotificationMessage: 'Permit approved',
          onSuccess: () => setActiveStep(2),
        },
        {
          label: 'Execute swap',
          callData: swapApproveCall,
          successNotificationMessage: 'Swap completed',
          refetchOnSuccess: true,
          onSuccess: (hash) => {
            setActiveStep(3);
            handleSuccess(hash);
          },
        },
      ];

  return (
    <Modal
      opened
      onClose={() => (activeStep === 3 ? onSuccess() : onClose())}
      size="480"
      centered
      withCloseButton={false}
      classNames={{
        root: 'bg-[#0F1012] shadow-[0_4px_4px_0_rgba(0,0,0,0.25)] rounded-[32px] border-[#202027]',
        content: 'backdrop-blur-[8px] py-6 px-6',
      }}
    >
      <Text mb={24} fz={24} fw={700} ta="center">
        {wrapOrUnwrapMonCall
          ? wrapOrUnwrapMonCall.type === 'wrap'
            ? 'Wrapping MON'
            : 'Unwrapping MON'
          : 'Swapping'}
      </Text>

      <div className="py-2 px-3 mb-6">
        <Group gap={8}>
          <Text fz={24} fw={700} c="#E3E3E8">
            {formatDecimal(approvedFromValue || fromValue, {
              withTruncate: true,
            })}
          </Text>
          <Token.Icon address={fromToken} size={24} />
          <Text fz={20} fw={700}>
            {fromTokenInfo?.symbol}
          </Text>
        </Group>
        <Token.Price
          tokenInfo={fromTokenInfo}
          amount={approvedFromValue || fromValue}
          textProps={{ mt: 8 }}
        />
        <Group
          className="w-7 h-7 my-[14px] rounded-full border-primarybrand border-[2px] border-solid"
          justify="center"
        >
          <IoArrowDownSharp size={16} color="var(--mantine-color-brand-0)" />
        </Group>
        <Group gap={8}>
          <Text fz={24} fw={700} c="#A0A0B1">
            {formatDecimal(approvedToValue || toValue, { withTruncate: true })}
          </Text>
          <Token.Icon address={toToken} size={24} />
          <Text fz={20} fw={700}>
            {toTokenInfo?.symbol}
          </Text>
        </Group>
        <Token.Price
          tokenInfo={toTokenInfo}
          amount={approvedToValue || toValue}
          textProps={{ mt: 8 }}
        />
      </div>

      <Stepper
        active={activeStep}
        orientation="vertical"
        allowNextStepsSelect={false}
        iconSize={24}
        classNames={{
          root: 'px-3',
          stepLabel: 'font-[600] leading-[24px]',
          content: '!px-0 !py-0',
        }}
      >
        {steps.map((step, index) => {
          const isActive = activeStep === index;

          return (
            <Stepper.Step
              key={step.label}
              label={step.label}
              loading={isActive}
              className={cn(
                activeStep === (wrapOrUnwrapMonCall ? 1 : 3) && 'hidden'
              )}
            >
              <ApprovalStep
                callData={step.callData}
                isActive={isActive}
                onSuccess={step.onSuccess}
                onError={onError}
              />
            </Stepper.Step>
          );
        })}

        <Stepper.Completed>
          <Stack justify="center">
            <Group gap={8} justify="center">
              <Text fz={20} fw={600} ta="center">
                {wrapOrUnwrapMonCall ? 'Transaction' : 'Swap'} completed
              </Text>
              <a
                href={`${NETWORK_VARS.explorer}/tx/${swapHash}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <BiLinkExternal
                  size={20}
                  color="var(--mantine-color-brand-0)"
                />
              </a>
            </Group>
            <Button variant="gradient" onClick={() => onSuccess()}>
              Done
            </Button>
          </Stack>
        </Stepper.Completed>
      </Stepper>
    </Modal>
  );
}

function ApprovalStep({
  callData,
  isActive,
  successNotificationMessage,
  refetchOnSuccess,
  onSuccess,
  onError,
}: {
  callData?: CallData;
  isActive: boolean;
  successNotificationMessage?: string;
  refetchOnSuccess?: boolean;
  onSuccess: (hash: string) => void;
  onError: (err: Error) => void;
}) {
  const {
    isPreparingTransaction,
    isSending,
    transactionError,
    sendTransaction,
  } = usePrepareAndSendTransaction({
    callData: callData,
    successNotificationMessage,
    refetchOnSuccess,
    onSuccess,
  });

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (isActive && !!sendTransaction && !isSending) {
        sendTransaction();
      }
    }, 100);

    return () => clearTimeout(timeout);
  }, [isActive, sendTransaction, isSending]);

  useEffect(() => {
    if (transactionError) {
      onError(transactionError);
    }
  }, [transactionError]);

  return null;
}
